/**
 * Accordion Custom Element
 * A reusable accordion component
 *
 * Structure:
 * <accordion-element values="item-1,item-2" [data-allow-multiple]>
 *   <div data-accordion-item value="item-1">
 *     <button data-accordion-trigger>
 *       Header
 *       <span data-accordion-icon>...</span>
 *     </button>
 *     <div data-accordion-content>
 *       Content
 *     </div>
 *   </div>
 * </accordion-element>
 *
 * Attributes:
 * - values: Comma-separated list of open item values
 * - value: Unique identifier for each item (on data-accordion-item)
 * - data-allow-multiple: If present, allows multiple items open at once
 *
 * Events:
 * - accordion:open
 * - accordion:close
 */

class AccordionElement extends HTMLElement {
  constructor() {
    super();

    this._openValues = new Set();

    // Save bound references for proper cleanup
    this._boundClick = this._handleClick.bind(this);
  }

  connectedCallback() {
    this.allowMultiple = this.hasAttribute("data-allow-multiple");

    // Parse initial open values from values attribute
    const valuesAttr = this.getAttribute("values") || "";
    if (valuesAttr) {
      const values = valuesAttr.split(",").map((v) => v.trim());

      if (!this.allowMultiple && values.length > 1) {
        console.error(
          "AccordionElement: Multiple values provided but data-allow-multiple is not set. Only the first value will be used.",
        );
        this._openValues.add(values[0]);
      } else {
        values.forEach((v) => this._openValues.add(v));
      }
    }

    this._bindEvents();
    this._initStates();
  }

  disconnectedCallback() {
    this._unbindEvents();
  }

  /**
   * Initialize accessibility attributes and states
   */
  _initStates() {
    const items = this.querySelectorAll("[data-accordion-item]");
    items.forEach((item) => {
      const value = item.getAttribute("value");
      if (!value) return;

      const isOpen = this._openValues.has(value);
      this._updateItemState(item, isOpen);
    });
  }

  _bindEvents() {
    this.addEventListener("click", this._boundClick);
  }

  _unbindEvents() {
    this.removeEventListener("click", this._boundClick);
  }

  _handleClick(e) {
    const trigger = e.target.closest("[data-accordion-trigger]");
    if (!trigger) return;

    // Prevent default button behavior
    e.preventDefault();

    const item = trigger.closest("[data-accordion-item]");
    if (!item) return;

    this.toggle(item);
  }

  toggle(item) {
    const value = item.getAttribute("value");
    if (!value) return;

    const isOpen = this._openValues.has(value);

    if (isOpen) {
      this.close(item);
    } else {
      this.open(item);
    }
  }

  open(item) {
    const value = item.getAttribute("value");
    if (!value) return;

    // If not allowing multiple, close all others first
    if (!this.allowMultiple) {
      this._closeAllExcept(value);
    }

    this._openValues.add(value);
    this._syncValuesAttribute();
    this._updateItemState(item, true);
    this._emitEvent("accordion:open", { item, value });
  }

  close(item) {
    const value = item.getAttribute("value");
    if (!value) return;

    this._openValues.delete(value);
    this._syncValuesAttribute();
    this._updateItemState(item, false);
    this._emitEvent("accordion:close", { item, value });
  }

  _closeAllExcept(exceptValue) {
    const items = this.querySelectorAll("[data-accordion-item]");
    items.forEach((item) => {
      const value = item.getAttribute("value");
      if (value && value !== exceptValue && this._openValues.has(value)) {
        this._openValues.delete(value);
        this._updateItemState(item, false);
      }
    });
  }

  closeAll() {
    const items = this.querySelectorAll("[data-accordion-item]");
    items.forEach((item) => {
      const value = item.getAttribute("value");
      if (value && this._openValues.has(value)) {
        this._openValues.delete(value);
        this._updateItemState(item, false);
      }
    });
    this._syncValuesAttribute();
  }

  _syncValuesAttribute() {
    this.setAttribute("values", Array.from(this._openValues).join(","));
  }

  _updateItemState(item, isOpen) {
    const trigger = item.querySelector("[data-accordion-trigger]");
    const content = item.querySelector("[data-accordion-content]");
    const icon = item.querySelector("[data-accordion-icon]");

    if (!trigger || !content) return;

    // Update item and trigger state
    const state = isOpen ? "open" : "closed";
    item.dataset.state = state;
    trigger.dataset.state = state;
    if (icon) icon.dataset.state = state;

    // Update ARIA
    trigger.setAttribute("aria-expanded", isOpen.toString());

    // Toggle Tailwind classes for visibility
    if (isOpen) {
      content.classList.remove("hidden");
      content.classList.add("block");
    } else {
      content.classList.remove("block");
      content.classList.add("hidden");
    }
  }

  _emitEvent(name, detail) {
    this.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        detail,
      }),
    );
  }
}

if (!customElements.get("accordion-element")) {
  customElements.define("accordion-element", AccordionElement);
}
